import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Download, Settings, Loader2, TrendingUp, IndianRupee, Store, Save } from 'lucide-react';

export default function DenFinancials() {
  const addToast = useToast();
  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('report');
  const [editSplits, setEditSplits] = useState({});
  const [saving, setSaving] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfg] = await Promise.all([apiFetch('/api/admin/report-config')]);
      setConfig(cfg);
      setEditSplits({ ...cfg.splits });
      await loadReport(cfg);
    } catch (e) {
      addToast(e.message, 'error');
    }
    setLoading(false);
  };

  const loadReport = async (cfg) => {
    const params = new URLSearchParams();
    if (dateRange.start) params.append('start_date', dateRange.start);
    if (dateRange.end) params.append('end_date', dateRange.end);
    const data = await apiFetch(`/api/admin/financials?${params}`);
    setReport(data);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const updated = await apiFetch('/api/admin/report-config', {
        method: 'PUT',
        body: JSON.stringify({ splits: editSplits }),
      });
      setConfig(updated);
      addToast('Report config saved!', 'success');
      await loadReport(updated);
    } catch (e) {
      addToast(e.message, 'error');
    }
    setSaving(false);
  };

  const exportCSV = () => {
    if (!report) return;
    const headers = ['Store', 'City', 'Type', 'Bookings', 'Total Revenue (₹)', 'HQ %', 'HQ Amount (₹)', 'Franchise Amount (₹)'];
    const rows = report.rows.map(r => [
      r.storeName, r.city, r.type, r.totalBookings,
      r.totalRevenue.toFixed(2), r.hqPercentage.toFixed(1),
      r.hqAmount.toFixed(2), r.franchiseAmount.toFixed(2)
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'petfolk-financial-report.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-petfolk-orange" /></div>;

  return (
    <div data-testid="den-financials">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Financial Reports</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Master Admin view — franchise revenue splits</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 p-1 rounded-2xl w-fit" style={{ background: 'var(--bg-secondary)' }}>
        {[{ k: 'report', l: 'Revenue Report', icon: TrendingUp }, { k: 'config', l: 'Split Config', icon: Settings }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={tab === t.k ? { background: 'var(--petfolk-orange)', color: 'white' } : { color: 'var(--text-secondary)' }}>
            <t.icon size={14} />{t.l}
          </button>
        ))}
      </div>

      {tab === 'report' && (
        <>
          {/* Date range filter */}
          <div className="flex gap-3 mb-6 items-end">
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>FROM</label>
              <input type="date" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))}
                className="rounded-xl px-3 py-2 text-sm" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            </div>
            <div>
              <label className="text-xs font-semibold block mb-1" style={{ color: 'var(--text-secondary)' }}>TO</label>
              <input type="date" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))}
                className="rounded-xl px-3 py-2 text-sm" style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            </div>
            <button onClick={() => loadReport(config)} className="px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: 'var(--petfolk-orange)' }}>Apply</button>
            <button onClick={() => { setDateRange({ start: '', end: '' }); loadReport(config); }} className="px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}>Clear</button>
          </div>

          {/* Summary KPIs */}
          {report && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Revenue', value: `₹${report.totals.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#10B981' },
                { label: 'Total Bookings', value: report.totals.totalBookings, color: '#3B82F6' },
                { label: 'Petfolk HQ Earnings', value: `₹${report.totals.totalHqAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#8B5CF6' },
                { label: 'Franchise Payouts', value: `₹${report.totals.totalFranchiseAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`, color: '#F59E0B' },
              ].map(k => (
                <div key={k.label} className="petfolk-card p-4">
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>{k.label.toUpperCase()}</p>
                  <p className="font-outfit text-xl font-bold" style={{ color: k.color }}>{k.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Revenue Table */}
          {report && (
            <div className="petfolk-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      {['Store', 'City', 'Type', 'Bookings', 'Revenue', 'HQ %', 'HQ (₹)', 'Franchise (₹)'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map(row => (
                      <tr key={row.nodeId} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text-primary)' }}>
                          <div className="flex items-center gap-2">
                            <Store size={14} style={{ color: 'var(--text-secondary)' }} />
                            {row.storeName}
                          </div>
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{row.city}</td>
                        <td className="px-4 py-3">
                          <span className="badge" style={{ background: row.type === 'STORE' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)', color: row.type === 'STORE' ? '#3B82F6' : '#10B981' }}>{row.type}</span>
                        </td>
                        <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{row.totalBookings}</td>
                        <td className="px-4 py-3 font-bold" style={{ color: '#10B981' }}>₹{row.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-4 py-3">
                          <span className="font-semibold" style={{ color: '#8B5CF6' }}>{row.hqPercentage.toFixed(1)}%</span>
                        </td>
                        <td className="px-4 py-3" style={{ color: '#8B5CF6' }}>₹{row.hqAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="px-4 py-3" style={{ color: '#F59E0B' }}>₹{row.franchiseAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    <tr style={{ background: 'var(--bg-secondary)' }}>
                      <td colSpan={3} className="px-4 py-3 font-bold" style={{ color: 'var(--text-primary)' }}>TOTAL</td>
                      <td className="px-4 py-3 font-bold" style={{ color: 'var(--text-primary)' }}>{report.totals.totalBookings}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#10B981' }}>₹{report.totals.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 font-bold" style={{ color: '#8B5CF6' }}>₹{report.totals.totalHqAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      <td className="px-4 py-3 font-bold" style={{ color: '#F59E0B' }}>₹{report.totals.totalFranchiseAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'config' && config && (
        <div className="max-w-xl">
          <div className="petfolk-card p-6">
            <h2 className="font-outfit font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Platform Fee Configuration</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Set the % Petfolk HQ retains from each franchise store's revenue. Franchise payout = 100% minus HQ %.</p>
            <div className="space-y-4">
              {Object.entries(editSplits).map(([nodeId, pct]) => (
                <div key={nodeId} className="flex items-center justify-between gap-4">
                  <label className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{nodeId}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={0} max={100} step={0.5}
                      value={pct}
                      onChange={e => setEditSplits(s => ({ ...s, [nodeId]: parseFloat(e.target.value) || 0 }))}
                      className="w-20 rounded-xl px-3 py-1.5 text-sm text-center font-semibold"
                      style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                    />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>% HQ | {(100 - pct).toFixed(1)}% franchise</span>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={saveConfig} disabled={saving} className="mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'var(--petfolk-orange)' }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Configuration
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
